import React, { useState, useEffect, useRef } from 'react';
import { 
  FiAlertTriangle, 
  FiEdit3, 
  FiTrash2, 
  FiUser, 
  FiCalendar,
  FiCamera
} from 'react-icons/fi';
import { DefectPin as DefectPinType } from '../../services/DefectsApiService';
import './DefectPin.css';

interface DefectPinProps {
  defect: DefectPinType;
  isSelected: boolean;
  zoomLevel: number;
  panPosition: { x: number; y: number };
  rotation: number;
  imageRef: React.RefObject<HTMLImageElement | null>;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const DefectPin: React.FC<DefectPinProps> = ({
  defect,
  isSelected,
  zoomLevel,
  panPosition,
  rotation,
  imageRef,
  onClick,
  onEdit,
  onDelete
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [pinPosition, setPinPosition] = useState({ x: 0, y: 0 });
  const pinRef = useRef<HTMLDivElement>(null);

  // CORRECTED: Calculate pin position based on relative coordinates and current transform
  useEffect(() => {
    if (!imageRef.current) return;

    const imageRect = imageRef.current.getBoundingClientRect();
    const containerRect = imageRef.current.parentElement?.getBoundingClientRect();

    if (!containerRect) return;

    // Calculate the actual image dimensions on screen (after zoom)
    const displayWidth = imageRect.width;
    const displayHeight = imageRect.height;

    // Convert normalized coordinates (0-1) to pixel position on the displayed image
    const x = defect.coordinates.x * displayWidth;
    const y = defect.coordinates.y * displayHeight;

    // Get the image's position relative to the container
    const imageOffsetX = imageRect.left - containerRect.left;
    const imageOffsetY = imageRect.top - containerRect.top;

    // Final pin position is image offset + relative position within image
    const finalX = imageOffsetX + x;
    const finalY = imageOffsetY + y;

    setPinPosition({ x: finalX, y: finalY });
  }, [defect.coordinates, zoomLevel, panPosition, rotation, imageRef]);

  // Get severity color
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#dc2626';
      case 'high': return '#ea580c';
      case 'medium': return '#d97706';
      case 'low': return '#16a34a';
      default: return '#6b7280';
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return '#ef4444';
      case 'in-progress': return '#f59e0b';
      case 'resolved': return '#10b981';
      case 'closed': return '#6b7280';
      default: return '#6b7280';
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Handle pin click
  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onClick();
  };

  // Handle action buttons
  const handleEdit = (event: React.MouseEvent) => {
    event.stopPropagation();
    onEdit();
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (window.confirm('Are you sure you want to delete this defect?')) {
      onDelete();
    }
  };

  return (
    <>
      {/* Pin Marker */}
      <div
        ref={pinRef}
        className={`defect-pin ${isSelected ? 'selected' : ''} severity-${defect.severity}`}
        style={{
          position: 'absolute',
          left: pinPosition.x - 12, // Center the pin (24px width / 2)
          top: pinPosition.y - 24,  // Position above the point (24px height)
          zIndex: isSelected ? 1000 : 100,
          transform: `rotate(${-rotation}deg)` // Counter-rotate to keep pin upright
        }}
        onClick={handleClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <div 
          className="pin-marker"
          style={{
            backgroundColor: getSeverityColor(defect.severity),
            borderColor: getStatusColor(defect.status)
          }}
        >
          <FiAlertTriangle size={12} color="white" />
        </div>

        {/* Pin Number/ID */}
        <div className="pin-number">
          #{defect.id?.slice(-4) || '0000'}
        </div>
      </div>

      {/* Tooltip */}
      {showTooltip && !isSelected && (
        <div
          className="defect-tooltip"
          style={{
            position: 'absolute',
            left: pinPosition.x + 15,
            top: pinPosition.y - 40,
            zIndex: 1001,
            transform: `rotate(${-rotation}deg)`
          }}
        >
          <div className="tooltip-content">
            <h4 className="tooltip-title">
              {defect.title || 'Untitled Defect'}
            </h4>
            <div className="tooltip-meta">
              <span className={`status-badge status-${defect.status}`}>
                {defect.status.replace('-', ' ')}
              </span>
              <span className={`severity-badge severity-${defect.severity}`}>
                {defect.severity}
              </span>
            </div>
            {defect.description && (
              <p className="tooltip-description">
                {defect.description.length > 100 
                  ? `${defect.description.slice(0, 100)}...`
                  : defect.description
                }
              </p>
            )}
          </div>
        </div>
      )}

      {/* Detailed Panel (when selected) */}
      {isSelected && (
        <div
          className="defect-detail-panel"
          style={{
            position: 'absolute',
            left: pinPosition.x + 20,
            top: pinPosition.y - 50,
            zIndex: 1002,
            transform: `rotate(${-rotation}deg)`
          }}
        >
          <div className="panel-header">
            <h3 className="panel-title">
              {defect.title || 'Untitled Defect'}
            </h3>
            <div className="panel-actions">
              <button
                className="action-btn edit-btn"
                onClick={handleEdit}
                title="Edit defect"
              >
                <FiEdit3 size={14} />
              </button>
              <button
                className="action-btn delete-btn"
                onClick={handleDelete}
                title="Delete defect"
              >
                <FiTrash2 size={14} />
              </button>
            </div>
          </div>

          <div className="panel-content">
            {/* Status and Severity */}
            <div className="status-row">
              <span className={`status-badge status-${defect.status}`}>
                {defect.status.replace('-', ' ')}
              </span>
              <span className={`severity-badge severity-${defect.severity}`}>
                {defect.severity} priority
              </span>
            </div>

            {/* Description */}
            {defect.description && (
              <div className="description-section">
                <p>{defect.description}</p>
              </div>
            )}

            {/* Trade and Category */}
            {(defect.trade || defect.category) && (
              <div className="meta-section">
                {defect.trade && (
                  <div className="meta-item">
                    <strong>Trade:</strong> {defect.trade}
                  </div>
                )}
                {defect.category && (
                  <div className="meta-item">
                    <strong>Category:</strong> {defect.category}
                  </div>
                )}
              </div>
            )}

            {/* Assignment */}
            {defect.assignedToName && (
              <div className="assignment-section">
                <FiUser size={14} />
                <span>Assigned to: {defect.assignedToName}</span>
              </div>
            )}

            {/* Photos */}
            {defect.photos && defect.photos.length > 0 && (
              <div className="photos-section">
                <div className="photos-header">
                  <FiCamera size={14} />
                  <span>{defect.photos.length} photo{defect.photos.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="photos-grid">
                  {defect.photos.slice(0, 3).map((photo, index) => (
                    <img
                      key={index}
                      src={photo}
                      alt={`Defect ${index + 1}`}
                      className="photo-thumbnail"
                      onClick={() => window.open(photo, '_blank')}
                    />
                  ))}
                  {defect.photos.length > 3 && (
                    <div className="photos-more">
                      +{defect.photos.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Dates */}
            <div className="dates-section">
              <div className="date-item">
                <FiCalendar size={12} />
                <span>Created: {formatDate(defect.createdAt)}</span>
              </div>
              {defect.updatedAt !== defect.createdAt && (
                <div className="date-item">
                  <FiCalendar size={12} />
                  <span>Updated: {formatDate(defect.updatedAt)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DefectPin;